import { useQuery } from "@tanstack/react-query";

import { VenuePagesRow } from "@punchup/venues/data/venue_pages";
import axios from "axios";
import { useTixologi } from "@punchup/data/legacy/hooks";
import { tixologiUrl } from "../../api-base";
import type { VenueBoxOfficeFee } from "../../models/box-office-fee";
import { BOX_OFFICE_FEE_QUERY_KEY } from "../../queries";

// The fee's types live in models/box-office-fee, beside the Box Office page that reads the same
// entry; they are re-exported here, where the settings hooks' callers have always imported them.
export type {
  BoxOfficeFeeChannel,
  BoxOfficeFeeType,
  VenueBoxOfficeFee,
} from "../../models/box-office-fee";
export { BOX_OFFICE_FEE_QUERY_KEY };

type UseBoxOfficeFeeReturn = {
  data: VenueBoxOfficeFee | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  hasTixologi: boolean;
  refetch: () => void;
};

export function useBoxOfficeFee({
  venuePage,
}: {
  venuePage: VenuePagesRow;
}): UseBoxOfficeFeeReturn {
  const { tixologiData } = useTixologi({
    id: venuePage.id,
    isComedian: false,
  });
  const hasTixologi = !!tixologiData?.token;
  // The token lookup itself is still in flight: report it as loading, so the block
  // doesn't flash "not selling through Punchup" for a venue that is.
  const isResolvingTixologi = tixologiData === undefined;

  const { data, isLoading, isError, error, refetch } =
    useQuery<VenueBoxOfficeFee>({
      queryKey: [BOX_OFFICE_FEE_QUERY_KEY, venuePage.id],
      queryFn: async (): Promise<VenueBoxOfficeFee> => {
        const response = await axios.get(
          tixologiUrl("/partners/box-office-fee"),
          { headers: { Authorization: `Bearer ${tixologiData?.token}` } }
        );
        return response.data;
      },
      enabled: hasTixologi,
    });

  return {
    data,
    isLoading: isResolvingTixologi || isLoading,
    isError,
    error,
    hasTixologi,
    refetch,
  };
}
